import {
    Banner,
    BannerDescription,
    BannerEyebrow,
    BannerImage,
    BannerImageContainer,
    BannerLinks,
    BannerSecondaryImage,
    BannerSecondaryImageContainer,
    BannerText,
    BannerTextContainer,
    BannerTitle,
    Box,
    Button,
    FluidImage,
    H1,
    Hidden,
    P,
} from '@vp/swan';
import { __, __image } from '@99designs/i18n';
import { Price, usePricingProviderContext } from '@99designs/pricing';
import { CloudinaryImage } from '../../CloudinaryImage';

interface HeaderProps {
    browseDesignServicesCtaOnClick: () => void;
    howItWorksCtaOnClick: () => void;
}

export function Header({ browseDesignServicesCtaOnClick, howItWorksCtaOnClick }: HeaderProps) {
    const { lowestPrice, loading: priceLoading } = usePricingProviderContext();

    return (
        <Banner
            editorial
            twoImages
            textBoxHorizontalAlign="center"
            backgroundColor="warm-white"
            data-testid="holiday-hero"
        >
            <BannerTextContainer>
                <BannerText data-testid="holiday-hero-content">
                    <BannerEyebrow data-testid="holiday-hero-eyebrow">
                        {__('Design Services')}
                    </BannerEyebrow>
                    <BannerTitle
                        as={H1}
                        fontSkin="title-headline"
                        fontWeight="normal"
                        data-testid="holiday-hero-title"
                    >
                        We'll do the design, you deck the halls
                    </BannerTitle>
                    <BannerDescription data-testid="holiday-hero-description">
                        <P>
                            Our pro designers can turn your ideas into beautiful, custom products -
                            perfectly crafted for any occasion.
                        </P>
                    </BannerDescription>
                    <Box fontSkin="title-item" data-testid="holiday-hero-price">
                        <Price price={lowestPrice} loading={priceLoading} />
                    </Box>
                    <BannerLinks data-testid="holiday-hero-actions">
                        <Button
                            skin="primary"
                            onClick={browseDesignServicesCtaOnClick}
                            data-testid="holiday-hero-browse-services-cta"
                        >
                            {__('Browse design services')}
                        </Button>
                        <Button
                            skin="secondary"
                            onClick={howItWorksCtaOnClick}
                            data-testid="holiday-hero-how-it-works-cta"
                        >
                            {__('How it works')}
                        </Button>
                    </BannerLinks>
                </BannerText>
            </BannerTextContainer>
            <BannerImageContainer data-testid="holiday-hero-image-primary">
                <BannerImage>
                    <Hidden xs sm md style={{ height: '100%' }}>
                        <CloudinaryImage
                            render={(props) => (
                                <FluidImage
                                    src={props.src}
                                    srcSet={props.srcSet}
                                    alt=""
                                    role="presentation"
                                />
                            )}
                            imagePath={__image('expert_services/HubPage/holiday/hero-image-right')}
                            width={686}
                        />
                    </Hidden>
                    <Hidden lg xl style={{ height: '100%' }}>
                        <CloudinaryImage
                            render={(props) => (
                                <FluidImage
                                    src={props.src}
                                    srcSet={props.srcSet}
                                    alt=""
                                    role="presentation"
                                />
                            )}
                            imagePath={__image('expert_services/HubPage/holiday/hero-image')}
                            width={686}
                        />
                    </Hidden>
                </BannerImage>
            </BannerImageContainer>
            <BannerSecondaryImageContainer data-testid="holiday-hero-image-secondary">
                <BannerSecondaryImage imageFocalPoint="right center">
                    <CloudinaryImage
                        render={(props) => (
                            <FluidImage
                                src={props.src}
                                srcSet={props.srcSet}
                                alt=""
                                role="presentation"
                            />
                        )}
                        imagePath={__image('expert_services/HubPage/holiday/hero-image-left')}
                        width={686}
                    />
                </BannerSecondaryImage>
            </BannerSecondaryImageContainer>
        </Banner>
    );
}
